<script setup>
  const consentStore = useConsentStore()
  const { t } = useI18n()

  const localCategories = ref({
    analytics: false,
    marketing: false
  })

  watch(
    () => consentStore.preferencesVisible,
    (visible) => {
      if (visible) {
        localCategories.value = {
          analytics: !!consentStore.categories.analytics,
          marketing: !!consentStore.categories.marketing
        }
      }
    }
  )

  const save = () => {
    consentStore.savePreferences(localCategories.value)
  }
</script>

<template>
  <Dialog
    v-model:visible="consentStore.preferencesVisible"
    modal
    :closable="true"
    dismissable-mask
    pt:root="max-w-2xl w-full mx-4"
    pt:header="!p-4"
    pt:content="!p-0"
  >
    <template #header>
      <div class="flex items-start gap-3">
        <div class="w-10 h-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
          <font-awesome :icon="['fas', 'cookie-bite']" />
        </div>
        <div>
          <p class="text-base font-semibold text-slate-900">{{ t('consent.centerTitle') }}</p>
          <p class="text-sm text-slate-600">{{ t('consent.centerSubtitle') }}</p>
        </div>
      </div>
    </template>

    <div class="p-5 space-y-4">
      <div
        class="rounded-xl border border-slate-200 bg-slate-50 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
      >
        <div class="space-y-1 flex-1">
          <p class="text-sm font-semibold text-slate-900">{{ t('consent.necessary') }}</p>
          <p class="text-sm text-slate-600">{{ t('consent.necessaryDesc') }}</p>
        </div>
        <span
          class="px-3 py-1 text-xs font-semibold rounded-full bg-slate-200 text-slate-700 whitespace-nowrap shrink-0"
        >
          {{ t('consent.alwaysActive') }}
        </span>
      </div>

      <div
        class="rounded-xl border border-slate-200 bg-white p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
      >
        <div class="space-y-1 flex-1">
          <p class="text-sm font-semibold text-slate-900">{{ t('consent.analytics') }}</p>
          <p class="text-sm text-slate-600">{{ t('consent.analyticsDesc') }}</p>
        </div>
        <div class="shrink-0">
          <ToggleSwitch v-model="localCategories.analytics" aria-label="Analytics consent" />
        </div>
      </div>

      <div
        class="rounded-xl border border-slate-200 bg-white p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
      >
        <div class="space-y-1 flex-1">
          <p class="text-sm font-semibold text-slate-900">{{ t('consent.marketing') }}</p>
          <p class="text-sm text-slate-600">{{ t('consent.marketingDesc') }}</p>
        </div>
        <div class="shrink-0">
          <ToggleSwitch v-model="localCategories.marketing" aria-label="Marketing consent" />
        </div>
      </div>

      <div class="flex flex-col sm:flex-row gap-2 justify-end pt-2">
        <button
          class="w-full sm:w-auto rounded-lg bg-slate-100 text-slate-800 border border-slate-200 px-4 py-2.5 text-sm font-semibold hover:bg-slate-200 transition-colors cursor-pointer"
          @click="consentStore.rejectAll()"
        >
          {{ t('consent.rejectAll') }}
        </button>
        <button
          class="w-full sm:w-auto rounded-lg bg-white text-sky-700 border border-sky-200 px-4 py-2.5 text-sm font-semibold hover:bg-sky-50 transition-colors cursor-pointer"
          @click="consentStore.acceptAll()"
        >
          {{ t('consent.acceptAll') }}
        </button>
        <button
          class="w-full sm:w-auto rounded-lg bg-sky-700 text-white px-4 py-2.5 text-sm font-semibold hover:bg-sky-800 transition-colors cursor-pointer shadow-sm"
          @click="save"
        >
          {{ t('consent.save') }}
        </button>
      </div>
    </div>
  </Dialog>
</template>
